/**
 * LES ÉTATS D'UNE ABSENCE — module PUR (05/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Le défaut qu'il corrige ────────────────────────────────────────────────
 *
 * « Dans le planning de Lamia, je vois *brouillon* à la suite de son congé
 * annuel du 30/09 » (RQ, 05/10/2026). Le congé était pourtant VALIDÉ.
 *
 * `Mon planning` affichait l'état de l'absence avec `StatePill`, qui porte le
 * vocabulaire du CIRCUIT DOCUMENTAIRE (brouillon, à vérifier, en vigueur…).
 * Une absence n'en parle pas la langue : ses états sont `demandee`, `validee`,
 * `refusee`, `annulee`. Aucun ne figurait dans la table des documents, et
 * `stateMeta` retombait en silence sur `brouillon` — le seul écran qui mêlait
 * les deux vocabulaires annonçait donc « Brouillon » sur un congé accordé.
 *
 * ⚠️ C'est un défaut de SILENCE, pas d'étiquette : le repli ne disait pas
 * qu'il ne connaissait pas l'état. Les deux moitiés sont corrigées — ici le
 * vocabulaire propre aux absences, et dans `meta.ts` le repli, qui écrit
 * désormais le code brut plutôt que d'inventer un état.
 *
 * ⚠️ MODULE SANS AUCUN IMPORT (feuille) : il est compilé et éprouvé par
 * `npm test` (préfixe `front/`), et une dépendance y amènerait tout le graphe
 * de l'application.
 */

export interface EtatAbsence {
  code: string;
  /** Le mot que l'écran écrit. */
  label: string;
  fond: string;
  texte: string;
}

/** Les quatre états d'une `rh-absence`, tels que le moteur les écrit.
 *  « En attente » et non « Demandée » : c'est ce qu'il reste à faire qui
 *  intéresse celui qui lit, pas le geste déjà fait. */
export const ETATS_ABSENCE: Record<string, EtatAbsence> = {
  demandee: { code: 'demandee', label: 'En attente', fond: '#FCF2E1', texte: 'var(--amber-600)' },
  validee: { code: 'validee', label: 'Validée', fond: '#E7F5EC', texte: 'var(--green-600)' },
  refusee: { code: 'refusee', label: 'Refusée', fond: '#FBE9EC', texte: 'var(--red-600, #c02942)' },
  annulee: { code: 'annulee', label: 'Annulée', fond: 'var(--gray-25)', texte: 'var(--text-faint)' },
};

/**
 * L'état à afficher. ⚠️ Un code INCONNU s'écrit TEL QUEL, en ton neutre : il
 * se voit, donc il se signale. Le remplacer par un état plausible est
 * exactement ce qui a fait lire « Brouillon » sur un congé validé.
 */
export function etatAbsence(statut: string): EtatAbsence {
  const code = String(statut || '').trim();
  return ETATS_ABSENCE[code]
    || { code, label: code || '—', fond: 'var(--gray-25)', texte: 'var(--text-muted)' };
}
